import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { IconButtonComponent, IconComponent, SearchInputComponent, TooltipDelay, TooltipDirective } from '@mode/capra';
import { ChartTypes } from '@mode/shared/contract-common';
import { BehaviorSubject, Subject, debounceTime, filter, takeUntil } from 'rxjs';
@Component({
  standalone: true,
  selector: 'mode-field-list-toolbar',
  imports: [CommonModule, IconButtonComponent, IconComponent, SearchInputComponent, TooltipDirective],
  templateUrl: './field-list-toolbar.component.html',
  styleUrls: ['./field-list-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldListToolbarComponent implements OnInit, OnChanges {
  @Input() readOnly = false;
  @Input() chartObjectType: ChartTypes.ObjectType = ChartTypes.ObjectType.Visualization;

  @Output() createCalcField = new EventEmitter<void>();
  @Output() searchTermChanged = new EventEmitter<string>();
  isExplorish: boolean | undefined;
  isExplorishOrViz: boolean | undefined;

  // tooltipSettings
  tooltipContent: 'New local calculated field' | 'New calculated field' = 'New calculated field';
  readonly tooltipInteractive = false;
  readonly tooltipAppendTo: Element | undefined;
  readonly tooltipDelay: TooltipDelay = 500;

  searchTerm = '';
  searchTerm$ = new BehaviorSubject('');
  private destroyed$ = new Subject<void>();

  constructor(element: ElementRef) {
    this.tooltipAppendTo = element.nativeElement;
  }

  ngOnInit(): void {
    this.isExplorish = this.isExploring(this.readOnly, this.chartObjectType);
    this.isExplorishOrViz = this.isExplorish || this.isViz();
    this.tooltipContent = this.isExplorish ? 'New local calculated field' : 'New calculated field';

    this.searchTerm$
      .pipe(
        takeUntil(this.destroyed$),
        filter((searchTerm) => searchTerm != null),
        debounceTime(400)
      )
      .subscribe((searchTerm: string) => {
        this.searchTerm = searchTerm;
        const lowercasedTerm = searchTerm.toLowerCase();
        this.searchTermChanged.emit(lowercasedTerm);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readOnly'] || changes['chartObjectType']) {
      this.isExplorish = this.isExploring(
        changes['readOnly']?.currentValue ?? this.readOnly,
        changes['chartObjectType']?.currentValue ?? this.chartObjectType
      );
      this.isExplorishOrViz = this.isExplorish || this.isViz();

      this.tooltipContent = this.isExplorish ? 'New local calculated field' : 'New calculated field';
    }
  }

  isExploring(readOnly: boolean, chartObjectType: ChartTypes.ObjectType): boolean {
    this.readOnly = readOnly;
    this.chartObjectType = chartObjectType;
    return !!(this.readOnly || this.chartObjectType === ChartTypes.ObjectType.Exploration);
  }

  isViz(): boolean {
    return !!(!this.readOnly && this.chartObjectType === ChartTypes.ObjectType.Visualization);
  }

  onCreateCalcField() {
    this.createCalcField.emit();
  }

  onSearchTermChanged(searchTerm: string) {
    this.searchTerm$.next(searchTerm);
  }
}
